import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import confetti from 'canvas-confetti';
import { analysis as analysisApi, profile as profileApi, progress as progressApi } from '@/lib/api';
import { useAuth } from './AuthContext';

const ProfileCtx = createContext(null);

const PENDING_JOB_STORAGE_KEY = 'sd_pending_market_job';
const PENDING_JD_JOB_STORAGE_KEY = 'sd_pending_jd_job';

function _loadPendingJob() {
  try {
    const raw = localStorage.getItem(PENDING_JOB_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.job_id) return null;
    // Expire pending jobs older than 10 minutes — safe default in case the
    // backend gave up on them.
    if (parsed.started_at && Date.now() - parsed.started_at > 10 * 60 * 1000) {
      localStorage.removeItem(PENDING_JOB_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch { return null; }
}
function _savePendingJob(j) {
  try {
    if (!j) localStorage.removeItem(PENDING_JOB_STORAGE_KEY);
    else localStorage.setItem(PENDING_JOB_STORAGE_KEY, JSON.stringify(j));
  } catch { /* no-op */ }
}

// ---------- JD job persistence ----------
// Mirrors the market-job pattern above but tracks the "single-JD-analysis in
// flight" so the analysis keeps running when the user leaves the JD Analyzer
// page and shows an "In progress" chip in the previous-analyses list.
function _loadPendingJdJob() {
  try {
    const raw = localStorage.getItem(PENDING_JD_JOB_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.job_id) return null;
    // Give JD jobs a generous 15 min ceiling — JD analysis is faster than
    // full profile analysis but still involves web_search.
    if (parsed.started_at && Date.now() - parsed.started_at > 15 * 60 * 1000) {
      localStorage.removeItem(PENDING_JD_JOB_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch { return null; }
}
function _savePendingJdJob(j) {
  try {
    if (!j) localStorage.removeItem(PENDING_JD_JOB_STORAGE_KEY);
    else localStorage.setItem(PENDING_JD_JOB_STORAGE_KEY, JSON.stringify(j));
  } catch { /* no-op */ }
}

export function ProfileProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [draft, setDraft] = useState({}); // Onboarding in-memory state
  const [savedProfile, setSavedProfile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [progress, setProgress] = useState({ completed_milestones: [], saved_jd_actions: [], completed_jd_actions: [], quick_wins: [] });
  const [internalLoading, setInternalLoading] = useState(true);
  const [loadedUserId, setLoadedUserId] = useState(null);

  // ---------- Market exploration state ----------
  // `activeMarketKey` = "" means "home market" (the user's profile location).
  // Non-empty = an explored market like "new york, us".
  const [activeMarketKey, setActiveMarketKey] = useState(() => {
    try { return localStorage.getItem('sd_active_market') || ''; } catch { return ''; }
  });
  const [markets, setMarkets] = useState([]); // [{market_key, target_market, work_authorized_target, is_home, created_at}]
  // Cached list of every saved (pivot_type × market_key) analysis for the user.
  // Populated by /api/analysis/pivots; drives the PivotSwitcher card so users
  // can jump between saved views (e.g. Level-up ↔ Shift-sideways) without
  // triggering a fresh LLM run.
  const [pivotAnalyses, setPivotAnalyses] = useState([]); // [{pivot_type, markets: [{market_key, is_home, created_at, target_market}]}]
  const [marketSwitching, setMarketSwitching] = useState(false);

  // ---------- Transition overlay state ----------
  // While `analysisTransitioning` is true, the UI keeps the current page visible
  // and paints a subtle "Refreshing your insights…" overlay on top. This prevents
  // the "first view → calibrated view" flip that was happening on:
  //   • New Assessment
  //   • Market switch (chip click)
  //   • Market re-analysis completion
  // Consumers should NOT clear `analysis` optimistically; we keep the old view
  // dimmed under the overlay so the user never sees a bare-empty state or a
  // half-rendered swap.
  const [analysisTransitioning, setAnalysisTransitioning] = useState(false);
  const [transitionLabel, setTransitionLabel] = useState('');
  const beginTransition = useCallback((label = 'Refreshing your insights…') => {
    setTransitionLabel(label);
    setAnalysisTransitioning(true);
  }, []);
  const endTransition = useCallback(() => {
    setAnalysisTransitioning(false);
    // Keep the label around briefly so the fade-out animation looks intentional
    setTimeout(() => setTransitionLabel(''), 400);
  }, []);

  // ---------- Pending market job (survives navigation via localStorage) ----------
  // `pendingMarketJob` = { job_id, target_market, work_authorized_target, started_at }
  // Polled here globally so market re-analyses complete correctly regardless
  // of whether the user stays on Role Explorer or navigates elsewhere.
  const [pendingMarketJob, setPendingMarketJob] = useState(() => _loadPendingJob());
  const [pendingProgressPct, setPendingProgressPct] = useState(0);
  const pollAbortRef = useRef(null);

  const setPending = useCallback((j) => {
    _savePendingJob(j);
    setPendingMarketJob(j);
    if (!j) setPendingProgressPct(0);
  }, []);

  // ---------- Pending JD job (survives navigation via localStorage) ----------
  // `pendingJdJob` = { job_id, jd_snippet, jd_title_hint, started_at }
  // When the user submits a JD in the JD Analyzer we track it here so the
  // background poll continues even when they navigate away. On completion we
  // stash the completed job id in `pendingJdCompletedId` so the JD Analyzer
  // page can pick it up on next mount and open the fresh result automatically.
  const [pendingJdJob, setPendingJdJob] = useState(() => _loadPendingJdJob());
  const [pendingJdCompletedId, setPendingJdCompletedId] = useState(null);
  const [pendingJdErrored, setPendingJdErrored] = useState(false);

  const startJdJob = useCallback((job_id, jd_snippet, jd_title_hint = '') => {
    const j = { job_id, jd_snippet: (jd_snippet || '').slice(0, 200), jd_title_hint, started_at: Date.now() };
    _savePendingJdJob(j);
    setPendingJdJob(j);
    setPendingJdCompletedId(null);
    setPendingJdErrored(false);
  }, []);

  const clearJdJob = useCallback(() => {
    _savePendingJdJob(null);
    setPendingJdJob(null);
  }, []);

  const consumeJdCompletedId = useCallback(() => {
    // Read-and-clear pattern so the JD Analyzer only auto-opens the fresh
    // result ONCE — subsequent visits show the analyses list as normal.
    const id = pendingJdCompletedId;
    setPendingJdCompletedId(null);
    return id;
  }, [pendingJdCompletedId]);

  const loading = internalLoading || (!!user && loadedUserId !== user?.user_id);

  const persistActiveMarket = useCallback((mk) => {
    try {
      if (mk) localStorage.setItem('sd_active_market', mk);
      else localStorage.removeItem('sd_active_market');
    } catch { /* no-op */ }
    setActiveMarketKey(mk || '');
  }, []);

  const reload = useCallback(async () => {
    if (authLoading) {
      setInternalLoading(true);
      return;
    }
    if (!user) {
      setSavedProfile(null);
      setAnalysis(null);
      setProgress({ completed_milestones: [], saved_jd_actions: [], completed_jd_actions: [], quick_wins: [] });
      setMarkets([]);
      setLoadedUserId(null);
      setInternalLoading(false);
      return;
    }
    setInternalLoading(true);
    try {
      // Include /pivots so the UI can render the pivot switcher card. It's a
      // lightweight endpoint (small doc, no LLM calls) so we always load it.
      // NOTE: pass the profile's current pivot_type so the backend applies
      // the strict pivot filter — otherwise a legacy pivot-less doc can leak
      // in as the "latest" analysis.
      const [p, a, prg, mk, pv] = await Promise.all([
        profileApi.get().catch(() => ({ profile: null })),
        analysisApi.latest({
          marketKey: activeMarketKey || undefined,
          pivotType: undefined, // let backend fall through to profile.pivot_type
        }).catch(() => ({ analysis: null })),
        progressApi.get().catch(() => ({ progress: null })),
        analysisApi.markets().catch(() => ({ markets: [] })),
        analysisApi.pivots().catch(() => ({ pivots: [] })),
      ]);
      setSavedProfile(p.profile);
      // Last-known-good rule (Q4 hardening): if the fetch returned NO analysis
      // for the current market/pivot but we already have one in state, KEEP the
      // existing analysis. Only clear it when we truly have nothing (first
      // load after login, user with no analyses at all). This prevents the
      // "click market chip → briefly null → DashboardLayout redirect" race.
      const nextAnalysis = a?.analysis ?? null;
      setAnalysis((prev) => {
        if (nextAnalysis) return nextAnalysis;
        if (prev) return prev;  // keep last-known-good
        return null;
      });
      // Auto-recover from a stale activeMarketKey: if the current market has
      // no analysis AND we're not on the home market, snap back to home so
      // the next reload picks up the home analysis. We DO NOT null the
      // analysis here — the snap-back triggers a fresh reload which will
      // load the home analysis into state without a visible flash.
      if (!nextAnalysis && activeMarketKey) {
        persistActiveMarket('');
      }
      setProgress(prg.progress || { completed_milestones: [], saved_jd_actions: [], completed_jd_actions: [], quick_wins: [] });
      setMarkets(mk?.markets || []);
      setPivotAnalyses(pv?.pivots || []);
      setLoadedUserId(user.user_id);
    } finally {
      setInternalLoading(false);
    }
  }, [user, authLoading, activeMarketKey, persistActiveMarket]);

  useEffect(() => { reload(); }, [reload]);

  // ---------- Market exploration actions ----------
  /**
   * Switch to a cached market instantly (no re-analysis). If no cache exists for
   * that market_key, this is a no-op (caller should use exploreMarket()).
   */
  const switchToMarket = useCallback(async (marketKey) => {
    // Show a subtle "Refreshing your insights…" overlay while the target market's
    // analysis is being fetched. Prevents the flip between old-market-data and
    // new-market-data.
    const targetMarket = markets.find((m) => m.market_key === (marketKey || ''));
    const label = targetMarket?.is_home
      ? `Reloading your home market view…`
      : `Calibrated for ${targetMarket?.target_market || 'your new market'}…`;
    beginTransition(label);
    persistActiveMarket(marketKey || '');
    try {
      // Explicitly fetch this market's analysis and swap it in ONCE. Avoids the
      // dual setAnalysis() race that used to fire when both reload() and this
      // path resolved at different times. Passes the profile's pivot_type so
      // the backend applies the strict pivot filter — otherwise switching to
      // a market that only has a different-pivot analysis would return null.
      const a = await analysisApi.latest({
        marketKey: marketKey || undefined,
        pivotType: savedProfile?.pivot_type || undefined,
      }).catch(() => ({ analysis: null }));
      // Last-known-good: only swap in the new analysis when we actually got
      // one. If the fetch returned null, keep the existing analysis visible
      // (the DashboardLayout guard would otherwise briefly redirect).
      if (a?.analysis) setAnalysis(a.analysis);
    } finally {
      // Small min-hold so the transition feels intentional even if the fetch
      // was cached and returned instantly.
      setTimeout(endTransition, 350);
    }
  }, [persistActiveMarket, markets, savedProfile?.pivot_type, beginTransition, endTransition]);

  /**
   * Switch to a saved pivot analysis (e.g. Level-up → Shift-sideways) WITHOUT
   * re-running the LLM if a cached analysis exists for that pivot on the
   * current market. Steps:
   *   1) Update the profile's `pivot_type` (persist the user's new choice).
   *   2) If a cached analysis for the new (pivot, current market) exists → swap
   *      it in instantly + refresh /pivots + /markets so the UI stays in sync.
   *   3) Otherwise return `{ needsAnalysis: true }` so the caller can kick off
   *      exploreMarket() / runAsync() for the fresh combo.
   *
   * Returns `{ cached: bool, needsAnalysis: bool }` so callers know what to do.
   */
  const switchPivot = useCallback(async (nextPivotType) => {
    if (!nextPivotType || nextPivotType === savedProfile?.pivot_type) {
      return { cached: false, needsAnalysis: false, noop: true };
    }
    const activeMk = activeMarketKey || '';
    // Check if we already have a saved analysis for the new (pivot, market) pair.
    const entry = pivotAnalyses.find((p) => p.pivot_type === nextPivotType);
    const cachedMarket = entry?.markets?.find((m) => (m.market_key || '') === activeMk);
    beginTransition(cachedMarket
      ? `Switching to your ${nextPivotType.replace('-', ' ')} view…`
      : `Preparing a fresh ${nextPivotType.replace('-', ' ')} analysis…`);
    try {
      // 1) Persist the new pivot_type on the profile (server-authoritative).
      const nextProfile = { ...(savedProfile || {}), pivot_type: nextPivotType };
      await profileApi.save(nextProfile).catch(() => null);
      setSavedProfile(nextProfile);

      if (cachedMarket) {
        // 2) Instant swap — fetch the saved analysis, refresh both lists.
        const [a, pv, mk] = await Promise.all([
          analysisApi.latest({ marketKey: activeMk || undefined, pivotType: nextPivotType }).catch(() => ({ analysis: null })),
          analysisApi.pivots().catch(() => ({ pivots: [] })),
          analysisApi.markets().catch(() => ({ markets: [] })),
        ]);
        if (a?.analysis) setAnalysis(a.analysis);
        setPivotAnalyses(pv?.pivots || []);
        setMarkets(mk?.markets || []);
        return { cached: true, needsAnalysis: false };
      }
      // 3) No cache for this (pivot, market) — DO NOT clear the previous
      //    analysis here. The DashboardLayout guard redirects to onboarding
      //    when analysis is null, which would kick the user out of the
      //    dashboard mid-switch. Keep the stale roles visible under the
      //    "Preparing a fresh …" transition banner; the global poller swaps
      //    in the new analysis once exploreMarket()'s job finishes.
      return { cached: false, needsAnalysis: true };
    } finally {
      setTimeout(endTransition, 350);
    }
  }, [savedProfile, activeMarketKey, pivotAnalyses, beginTransition, endTransition]);

  /**
   * Kick off an analysis for a brand-new market. Returns the job_id so the caller
   * can poll. Once done, the caller should call refreshMarkets() to update state.
   */
  const exploreMarket = useCallback(async ({ target_market, work_authorized_target, pivot_type }) => {
    setMarketSwitching(true);
    try {
      const homeExists = markets.some((m) => m.is_home);
      let job_id;
      let mode = 'full';
      let refreshErr = null;
      if (homeExists) {
        try {
          const resp = await analysisApi.refreshMarket({
            target_market,
            work_authorized_target: !!work_authorized_target,
            ...(pivot_type ? { pivot_type } : {}),
          });
          job_id = resp?.job_id;
          if (job_id) mode = 'refresh';
        } catch (err) {
          const status = err?.response?.status;
          refreshErr = err;
          if (status === 401) throw err;
        }
      }
      if (!job_id) {
        try {
          const resp = await analysisApi.runAsync({
            target_market,
            work_authorized_target: !!work_authorized_target,
            ...(pivot_type ? { pivot_type } : {}),
          });
          job_id = resp.job_id;
          mode = 'full';
        } catch (fullErr) {
          // If BOTH paths failed, surface the more informative of the two
          // errors (backend `detail` field wins over generic "Network Error").
          const refreshDetail = refreshErr?.response?.data?.detail;
          const fullDetail = fullErr?.response?.data?.detail;
          if (refreshDetail && !fullDetail) {
            // Rehydrate the fuller error onto the thrown object so the caller
            // can show something useful.
            fullErr.response = fullErr.response || {};
            fullErr.response.data = { detail: refreshDetail };
          }
          throw fullErr;
        }
      }
      // Persist pending job so the poll survives navigation between pages.
      setPending({
        job_id,
        target_market,
        work_authorized_target: !!work_authorized_target,
        started_at: Date.now(),
        mode,
      });
      setPendingProgressPct(6);
      return job_id;
    } catch (e) {
      setMarketSwitching(false);
      throw e;
    }
  }, [setPending, markets]);

  /**
   * After a market analysis job finishes, call this to refresh markets + analysis
   * data and activate the new market.
   */
  const activateExploredMarket = useCallback(async (marketKey) => {
    // Called by the global poll effect when a new-market analysis completes.
    // Show the "Calibrated for [City]…" overlay while we swap in the freshly-
    // baked analysis. Keep the current page visible so it dims under the
    // overlay rather than flashing empty.
    const nextTarget = pendingMarketJob?.target_market;
    const wasRefresh = pendingMarketJob?.mode === 'refresh';
    const label = marketKey
      ? (wasRefresh
          ? `Recalibrated for ${nextTarget || 'your new market'}…`
          : `Calibrated for ${nextTarget || 'your new market'}…`)
      : 'Reloading your home market view…';
    beginTransition(label);
    persistActiveMarket(marketKey || '');
    setMarketSwitching(false);
    try {
      const [mk, a, pv] = await Promise.all([
        analysisApi.markets().catch(() => ({ markets: [] })),
        // Fetch WITH the profile's current pivot_type so we get the freshly
        // baked analysis for this pivot (backend defaults to the profile's
        // pivot_type when none is passed — but we've just switched profile
        // state, so pass the current one explicitly to avoid any race).
        analysisApi.latest({
          marketKey: marketKey || undefined,
          pivotType: (savedProfile?.pivot_type || undefined),
        }).catch(() => ({ analysis: null })),
        analysisApi.pivots().catch(() => ({ pivots: [] })),
      ]);
      setMarkets(mk?.markets || []);
      setPivotAnalyses(pv?.pivots || []);
      if (a?.analysis) setAnalysis(a.analysis);
    } finally {
      // Slightly longer hold on market switches so the "Calibrated for X…" tag
      // has time to breathe (per user preference: polished transition).
      setTimeout(endTransition, 700);
    }
  }, [persistActiveMarket, pendingMarketJob?.target_market, savedProfile?.pivot_type, beginTransition, endTransition]);

  // ---------- Global poll effect for pending market jobs ----------
  // Runs whenever `pendingMarketJob.job_id` changes. Because ProfileContext is
  // mounted for the entire authenticated session, polling continues across
  // page navigation. Persists to localStorage so a full page refresh mid-poll
  // resumes correctly.
  useEffect(() => {
    if (!pendingMarketJob?.job_id || !user) return;
    let cancelled = false;
    let ticks = 0;
    const iv = setInterval(async () => {
      ticks += 1;
      setPendingProgressPct((p) => Math.min(95, p + Math.max(1, Math.round((95 - p) * 0.08))));
      try {
        const j = await analysisApi.jobStatus(pendingMarketJob.job_id);
        if (cancelled) return;
        if (j.status === 'done') {
          clearInterval(iv);
          setPendingProgressPct(100);
          const mk = j.result?.market_key || '';
          setTimeout(async () => {
            if (cancelled) return;
            await activateExploredMarket(mk);
            setPending(null);
          }, 400);
        } else if (j.status === 'error') {
          clearInterval(iv);
          setPending(null);
          setMarketSwitching(false);
          // Silent failure — the UI will surface via `pendingMarketJob === null` returning
          // to normal. A more granular error state can be exposed if needed.
        }
      } catch (e) { /* transient — retry next tick */ }
      if (ticks > 150) { // ~5 min safety cutoff (each tick = 2s)
        clearInterval(iv);
        setPending(null);
        setMarketSwitching(false);
      }
    }, 2000);
    pollAbortRef.current = () => { cancelled = true; clearInterval(iv); };
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingMarketJob?.job_id, user?.user_id, activateExploredMarket]);

  const deleteMarket = useCallback(async (marketKey) => {
    if (!marketKey) return;
    await analysisApi.deleteMarket(marketKey).catch(() => null);
    // If deleting the currently active market, snap back to home
    if (activeMarketKey === marketKey) {
      persistActiveMarket('');
    }
    const mk = await analysisApi.markets().catch(() => ({ markets: [] }));
    setMarkets(mk?.markets || []);
  }, [activeMarketKey, persistActiveMarket]);

  // ---------- Global poll effect for pending JD jobs ----------
  // Runs whenever `pendingJdJob.job_id` changes. Because ProfileContext is
  // mounted for the entire authenticated session, this poll continues even
  // if the user leaves the JD Analyzer page. On completion we set
  // `pendingJdCompletedId` so the JD Analyzer picks up the fresh result on
  // its next render (or, if the user is still there, immediately).
  useEffect(() => {
    if (!pendingJdJob?.job_id || !user) return;
    let cancelled = false;
    let ticks = 0;
    const iv = setInterval(async () => {
      ticks += 1;
      try {
        const j = await analysisApi.jobStatus(pendingJdJob.job_id);
        if (cancelled) return;
        if (j.status === 'done') {
          clearInterval(iv);
          // The JD worker persists into the jd_analyses collection and
          // returns `{ id: doc_id, result: {...} }` in the job result (see
          // analysis_api.py _run_jd_worker). Use that id so the JD Analyzer
          // can auto-open the fresh result on its next render.
          const completedId = j.result?.id || j.result?.analysis_id || pendingJdJob.job_id;
          setPendingJdCompletedId(completedId);
          _savePendingJdJob(null);
          setPendingJdJob(null);
          // NOTE: the "jd_ran" Quick Win is deliberately NOT fired here
          // anymore. Per spec it fires only when the benchmark result
          // actually RENDERS (see the render-effect in JDAnalyzer.jsx) —
          // not on button click, not on background completion.
        } else if (j.status === 'error') {
          clearInterval(iv);
          setPendingJdErrored(true);
          _savePendingJdJob(null);
          setPendingJdJob(null);
        }
      } catch (e) {
        console.warn('[jd-poll] status check failed (will retry):', e?.message || e);
      }
      if (ticks > 200) { // ~10 min safety cutoff (each tick = 3s)
        clearInterval(iv);
        setPendingJdErrored(true);
        _savePendingJdJob(null);
        setPendingJdJob(null);
      }
    }, 3000);
    return () => { cancelled = true; clearInterval(iv); };
    // The auth user object exposes `user_id` (NOT `id`) — depending on
    // `user?.id` (always undefined) meant this effect never re-ran after
    // auth hydrated on a page refresh, so restored-from-localStorage JD jobs
    // were never polled.
  }, [pendingJdJob?.job_id, user?.user_id]);

  const patchDraft = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const resetDraft = () => setDraft({});

  const toggleMilestone = async (key) => {
    // Optimistic: flip the checkbox instantly, but roll back if the write
    // fails. Wrapped in try/catch so a transient 4xx / 5xx / network blip
    // cannot escape as an "Uncaught runtime error" red modal in dev mode
    // (the onClick handler that calls this is NOT async and does not await).
    const prev = progress.completed_milestones || [];
    const next = prev.includes(key)
      ? prev.filter(k => k !== key)
      : [...prev, key];
    setProgress(p => ({ ...p, completed_milestones: next }));
    try {
      await progressApi.update({ completed_milestones: next });
    } catch (_e) {
      setProgress(p => ({ ...p, completed_milestones: prev }));
      try { const { toast } = await import('sonner'); toast.error("Couldn't save that — please try again."); } catch (_x) { /* no-op */ }
    }
  };

  // Toggle whether an action is IN the plan (saved list).
  // NEW: This never touches completion state. Users complete an action in
  // My Progress via toggleJdActionCompletion below. If we're REMOVING an
  // action from the plan, we also drop it from the completed list to keep
  // the two arrays consistent (mirrors backend behaviour).
  const toggleJdAction = async (key) => {
    const prevSaved = progress.saved_jd_actions || [];
    const prevCompleted = progress.completed_jd_actions || [];
    const isSaved = prevSaved.includes(key);
    const nextSaved = isSaved ? prevSaved.filter(k => k !== key) : [...prevSaved, key];
    const nextCompleted = isSaved ? prevCompleted.filter(k => k !== key) : prevCompleted;
    setProgress(p => ({ ...p, saved_jd_actions: nextSaved, completed_jd_actions: nextCompleted }));
    try {
      await progressApi.update({ saved_jd_actions: nextSaved, completed_jd_actions: nextCompleted });
    } catch (_e) {
      setProgress(p => ({ ...p, saved_jd_actions: prevSaved, completed_jd_actions: prevCompleted }));
      try { const { toast } = await import('sonner'); toast.error("Couldn't save that — please try again."); } catch (_x) { /* no-op */ }
    }
  };

  // Toggle whether a saved JD action is marked complete. Only meaningful for
  // keys that are already in saved_jd_actions.
  const toggleJdActionCompletion = async (key) => {
    const saved = progress.saved_jd_actions || [];
    if (!saved.includes(key)) return; // nothing to complete if it isn't in the plan
    const prev = progress.completed_jd_actions || [];
    const next = prev.includes(key)
      ? prev.filter(k => k !== key)
      : [...prev, key];
    setProgress(p => ({ ...p, completed_jd_actions: next }));
    try {
      await progressApi.update({ completed_jd_actions: next });
    } catch (_e) {
      setProgress(p => ({ ...p, completed_jd_actions: prev }));
      try { const { toast } = await import('sonner'); toast.error("Couldn't save that — please try again."); } catch (_x) { /* no-op */ }
    }
  };

  // Auto-tracked Quick Win completion. Idempotent + sticky per-user. Returns
  // { newly_completed } so callers can trigger a toast only once. Confetti
  // fires here — centralised so the celebration happens on WHATEVER page the
  // user is on when the completion event occurs (JD Analyzer, Role Explorer,
  // Network In). Otherwise the confetti would only fire when the user later
  // navigates to My Progress, by which time the celebration feels stale.
  const markQuickWin = useCallback(async (key) => {
    const current = progress.quick_wins || [];
    if (current.includes(key)) return { newly_completed: false, quick_wins: current };
    // Optimistic local update — server call may lag on flaky networks but
    // the UI should reflect the completion instantly.
    const next = [...current, key];
    setProgress(p => ({ ...p, quick_wins: next }));
    // Big center-stage confetti burst so the celebration is impossible to
    // miss regardless of which page the user is on. Two overlapping bursts
    // from slightly offset origins for a fuller effect + one high-arc
    // starburst.
    try {
      const palette = ['#4CD964', '#3AA856', '#1F4D2A', '#F2B860', '#0A0A0A', '#FFFFFF'];
      const defaults = {
        colors: palette,
        disableForReducedMotion: true,
        zIndex: 9999,
        gravity: 0.9,
      };
      // Main burst from centre — big and wide.
      confetti({
        ...defaults,
        particleCount: 140,
        spread: 100,
        startVelocity: 55,
        ticks: 200,
        scalar: 1.1,
        origin: { x: 0.5, y: 0.45 },
      });
      // Left burst — slight offset to fill the horizontal spread.
      confetti({
        ...defaults,
        particleCount: 70,
        spread: 70,
        startVelocity: 45,
        ticks: 200,
        scalar: 1.0,
        origin: { x: 0.3, y: 0.55 },
      });
      // Right burst — mirrors the left.
      confetti({
        ...defaults,
        particleCount: 70,
        spread: 70,
        startVelocity: 45,
        ticks: 200,
        scalar: 1.0,
        origin: { x: 0.7, y: 0.55 },
      });
      // Slow high-arc "starburst" after a beat so the effect lingers.
      setTimeout(() => {
        try {
          confetti({
            ...defaults,
            particleCount: 45,
            spread: 130,
            startVelocity: 30,
            ticks: 260,
            scalar: 1.3,
            gravity: 0.55,
            origin: { x: 0.5, y: 0.5 },
          });
        } catch (_e) { /* no-op */ }
      }, 220);
    } catch (_e) { /* no-op — confetti is a nice-to-have, never breaks flows */ }
    try {
      const data = await progressApi.markQuickWin(key);
      return { newly_completed: !!data.newly_completed, quick_wins: data.quick_wins || next };
    } catch (_e) {
      return { newly_completed: true, quick_wins: next };
    }
  }, [progress.quick_wins]);

  // Unmark a Quick Win manually. Per user request: users CAN un-tick an
  // auto-completed row (e.g. they want to redo it, or the auto-tracker fired
  // by mistake). Sticky server-side too — we persist the removal so it
  // survives a reload.
  const unmarkQuickWin = useCallback(async (key) => {
    const current = progress.quick_wins || [];
    if (!current.includes(key)) return;
    const next = current.filter(k => k !== key);
    setProgress(p => ({ ...p, quick_wins: next }));
    try {
      await progressApi.update({ quick_wins: next });
    } catch (_e) { /* optimistic — no rollback needed */ }
  }, [progress.quick_wins]);

  // Keep a live ref to markQuickWin so the JD polling interval (which
  // captures state via closure) can always call the FRESHEST version — never
  // a stale copy that would race with `progress.quick_wins`.
  const markQuickWinRef = useRef(markQuickWin);
  useEffect(() => { markQuickWinRef.current = markQuickWin; }, [markQuickWin]);

  return (
    <ProfileCtx.Provider value={{
      draft, patchDraft, resetDraft,
      savedProfile, setSavedProfile,
      analysis, setAnalysis,
      progress, toggleMilestone, toggleJdAction, toggleJdActionCompletion, markQuickWin, unmarkQuickWin,
      loading, reload,
      // Market exploration
      activeMarketKey,
      markets,
      marketSwitching,
      switchToMarket,
      exploreMarket,
      activateExploredMarket,
      deleteMarket,
      // Pivot switcher — flip between saved (pivot × market) analyses
      pivotAnalyses,
      switchPivot,
      // Global market-job polling (survives navigation)
      pendingMarketJob,
      pendingProgressPct,
      // Global JD-job polling (survives navigation)
      pendingJdJob,
      pendingJdCompletedId,
      pendingJdErrored,
      startJdJob,
      clearJdJob,
      consumeJdCompletedId,
      // Transition overlay state ("Refreshing your insights…" / "Calibrated for [City]…")
      analysisTransitioning,
      transitionLabel,
    }}>
      {children}
    </ProfileCtx.Provider>
  );
}

export function useProfileState() {
  const ctx = useContext(ProfileCtx);
  if (!ctx) throw new Error('useProfileState must be used inside ProfileProvider');
  return ctx;
}
